package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.db.{TranslationData, TranslationEngineData}
import com.neurobs.ccab.translation.{TranslationRequestData, TranslationResponseData}
import com.neurobs.ccabserver.translation.util.PhraseTranslationManager
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{BooleanAttribute, GenericState, GenericStateGenerator, RegisteredChangeAttribute, ServerDataAttribute}
import com.neurobs.util.SecondsTimestamp
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

case class PhraseTranslationState (
                                    phraseSelectionState: PhraseSelectionState,
                                    userTranslationState: ServerDataAttribute[TranslationData],
                                    savingUserTranslationState: BooleanAttribute,
                                    backTranslationState: ServerDataAttribute[TranslationResponseData],
                                    loadingBackTranslationState: BooleanAttribute,
                                    registeredChangeState: RegisteredChangeAttribute
                                  ) extends GenericState {

  def newUserTranslationData(phraseId: Long, languageId: Long, translatorId: Long) = {
    TranslationData(-1, SecondsTimestamp.now(), phraseId, languageId, None, Some(translatorId), "", None)
  }

  def registerChange() = registeredChangeState.register()

  def selectedPhrase = phraseSelectionState.selectedPhrase

  def userTranslation = userTranslationState.value
  def userTranslationText = userTranslation.map(_.text).toOption.getOrElse("")
  def setUserTranslation(translationData: TranslationData) = userTranslationState.setData(translationData)
  def updateUserTranslationText(translationText: String) = {
    userTranslation match {
      case Left(_) =>
      case Right(translationData) => setUserTranslation(translationData.copy(text = translationText))
    }
    registerChange()
  }

  def backTranslation = backTranslationState.value
  def setBackTranslation(translationResponseData: TranslationResponseData) = backTranslationState.setData(translationResponseData)
  def clearBackTranslation() = backTranslationState.reset()

  def awaitingServerUserTranslation = userTranslationState.loading
  def savingUserTranslation = savingUserTranslationState.value

  def awaitingBackTranslation = loadingBackTranslationState.value

  def loadUserTranslation(phraseTranslationManager: PhraseTranslationManager, languageId: Long, translatorId: Long) = {
    val phraseId = phraseTranslationManager.phraseId
    TranslationServer.getUserPhraseTranslation(phraseTranslationManager.phraseId, translatorId)(handleServerUserTranslation(phraseId, languageId, translatorId))
  }

  def handleServerUserTranslation(phraseId: Long, languageId: Long, translatorId: Long) = ServerResponseUtil.handleObjectResponse[Option[TranslationData]](
    handleObject = _ match {
      case None =>
        setUserTranslation(newUserTranslationData(phraseId, languageId, translatorId))
      case Some(userTranslation) =>
        setUserTranslation(userTranslation)
    },
    onError = userTranslationState.setError(_)
  )

  def saveUserTranslation(): Unit = {
    userTranslation match {
      case Left(_) => console.error("Cannot save user translation before it is set")
      case Right(translationData) =>
        savingUserTranslationState.setValue(true)
        if (translationData.id == -1) TranslationServer.createUserTranslation(translationData)(handleServerTranslationSave)
        else TranslationServer.updateUserTranslation(translationData)(handleServerTranslationSave)
    }
  }

  def handleServerTranslationSave = ServerResponseUtil.handleObjectResultResponse[TranslationData](
    handleObject = data => {
      setUserTranslation(data)
      phraseSelectionState.noteTranslation(data.phraseId)
      phraseSelectionState.nextPhrase()
    },
    onAnyError = userTranslationState.setError(_),
    onAnyResponse = () => savingUserTranslationState.setValue(false)
  )

  def backTranslate(sourceLanguage: String, text: String) = {
    val requestData = TranslationRequestData(sourceLanguage, "english", text)
    loadingBackTranslationState.setValue(true)
    TranslationServer.getTextTranslation(requestData)(handleBackTranslation)
  }

  def handleBackTranslation = ServerResponseUtil.handleObjectResultResponse[TranslationResponseData](
    handleObject = setBackTranslation(_),
    onAnyError = backTranslationState.setError(_),
    onAnyResponse = () => loadingBackTranslationState.setValue(false)
  )

}


object PhraseTranslationStateGenerator extends GenericStateGenerator[PhraseTranslationState] {
  override def generateEmptyDefaultState(): PhraseTranslationState = PhraseTranslationState(
    PhraseSelectionStateGenerator.generateEmptyDefaultState(),
    emptyServerDataState[TranslationData](),
    emptyBooleanState(),
    emptyServerDataState[TranslationResponseData](),
    emptyBooleanState(),
    emptyRegisteredChangeState()
  )
}

@react
object PhraseTranslationStateProvider {
  case class Props(children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val translatorState = TranslatorStateGenerator.accessState
    val phraseSelectionState = PhraseSelectionStateGenerator.accessState
    val userTranslationState = ServerDataAttribute(useState[Either[Option[String], TranslationData]](Left(None)))
    val savingUserTranslationState = BooleanAttribute(useState(false))
    val backTranslationState = ServerDataAttribute(useState[Either[Option[String], TranslationResponseData]](Left(None)))
    val loadingBackTranslationState = BooleanAttribute(useState(false))
    val registeredChangeState = RegisteredChangeAttribute(useState(0))

    val phraseTranslationState = PhraseTranslationState(
      phraseSelectionState = phraseSelectionState,
      userTranslationState = userTranslationState,
      savingUserTranslationState = savingUserTranslationState,
      backTranslationState = backTranslationState,
      loadingBackTranslationState = loadingBackTranslationState,
      registeredChangeState = registeredChangeState
    )

    useEffect(() => {
      backTranslationState.reset()
    }, Vector(userTranslationState.value))

    useEffect(() => {
      userTranslationState.reset()
      translatorState.selectedTranslator match {
        case None =>
        case Some(translator) =>
          phraseSelectionState.selectedPhrase match {
            case None =>
            case Some(phraseTranslationManager) =>
              phraseTranslationState.loadUserTranslation(phraseTranslationManager, translator.languageId, translator.id)
          }
      }
    }, Vector(phraseSelectionState.selectedPhrase))

    PhraseTranslationStateGenerator.Provider(phraseTranslationState)(
      props.children:_*
    )
  }
}